import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Folder, Project } from '@/lib/types'
import { cn, LIST_COLORS, projectBubbleClass } from '@/lib/utils'
import { APP_VERSION } from '@/lib/version'
import { useConfirm } from './ConfirmDialog'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  MoreVertical,
  Pencil,
  Archive,
  ArchiveRestore,
  Trash2,
  Check,
  X,
  Loader2,
  AlertCircle,
  StopCircle,
  GripVertical,
  Users,
  Plus,
  SlidersHorizontal,
  Flame,
  FolderPlus,
  Folder as FolderIcon,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  UserPlus2,
  Focus,
} from 'lucide-react'

interface Props {
  projects: Project[]
  selectedId: string | null
  currentUserId: string
  onSelect: (id: string) => void
  ticketCounts: Record<string, number>
  newCountByProject: Record<string, number>
  onRename: (id: string, name: string) => void | Promise<void>
  onArchive: (id: string, archived: boolean) => void | Promise<void>
  onDelete: (id: string) => void | Promise<void>
  /** Open the per-channel settings (AI tone + reply permission). */
  onOpenSettings?: (id: string) => void
  /** Open the « invite to all cards » dialog for a channel. */
  onInviteToCards?: (id: string) => void
  /** Projects currently playing the « new message » bounce. */
  bouncingProjectIds?: Set<string>
  /** Flame colour per project: 'red' = has an urgent card, 'orange' = has
   *  a high-priority card (urgent wins). Absent = no flame. */
  projectFlames?: Map<string, 'red' | 'orange'>
  /** Persist a new ordering of the projects list (sidebar drag-and-drop). */
  onReorder?: (orderedIds: string[]) => void | Promise<void>
  /** Create a fresh manual (non-WhatsApp) project. Opens an inline
   *  composer in the sidebar header — confirm with Enter, cancel with
   *  Escape. App.tsx seeds three default lists and auto-switches. */
  onCreateProject?: (name: string) => Promise<void> | void
  /** Folders grouping channels (personal organisation layer). */
  folders?: Folder[]
  onCreateFolder?: (name: string) => Promise<void> | void
  onRenameFolder?: (id: string, name: string) => Promise<void> | void
  onSetFolderColor?: (id: string, color: string | null) => Promise<void> | void
  onReorderFolders?: (orderedIds: string[]) => Promise<void> | void
  onDeleteFolder?: (id: string) => Promise<void> | void
  /** Move a channel into a folder (null = ungrouped). */
  onAssignFolder?: (projectId: string, folderId: string | null) => void | Promise<void>
  /** True while a board card is being dragged — channels that can't accept
   *  a card (real WhatsApp numbers) grey out; personal channels stay lit. */
  cardDragActive?: boolean
  showArchived: boolean
  archivedCount: number
  onToggleArchived: () => void
}

const AVATAR_PALETTE = [
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-fuchsia-100 text-fuchsia-700',
  'bg-teal-100 text-teal-700',
  'bg-orange-100 text-orange-700',
]
function avatarColor(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]
}
function initials(name: string): string {
  const cleaned = name.replace(/[^\p{L}\p{N}\s+]/gu, ' ').trim()
  if (!cleaned) return '?'
  return (
    cleaned
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join('') || '?'
  )
}

export function ProjectSidebar({
  projects,
  selectedId,
  currentUserId,
  onSelect,
  ticketCounts,
  newCountByProject,
  onRename,
  onArchive,
  onDelete,
  onOpenSettings,
  onInviteToCards,
  bouncingProjectIds,
  projectFlames,
  onReorder,
  onCreateProject,
  folders = [],
  onCreateFolder,
  onRenameFolder,
  onSetFolderColor,
  onReorderFolders,
  onDeleteFolder,
  onAssignFolder,
  cardDragActive = false,
  showArchived,
  archivedCount,
  onToggleArchived,
}: Props) {
  const { t } = useTranslation()
  const [menuFor, setMenuFor] = useState<string | null>(null)
  // Whole-sidebar collapse → icon-only rail (persisted).
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sidebarCollapsed') === '1'
    } catch {
      return false
    }
  })
  function toggleCollapsed() {
    setCollapsed((v) => {
      const next = !v
      try {
        localStorage.setItem('sidebarCollapsed', next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }
  // New-folder inline composer + collapse state (persisted).
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [folderDraft, setFolderDraft] = useState('')
  const folderInputRef = useRef<HTMLInputElement | null>(null)

  // « Client view » — when on, hide every project except the one currently
  // selected (useful for screen-sharing with a customer). Persisted in
  // localStorage so it survives reloads.
  const [clientMode, setClientMode] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sidebarClientMode') === '1'
    } catch {
      return false
    }
  })
  function toggleClientMode() {
    setClientMode((v) => {
      const next = !v
      try {
        localStorage.setItem('sidebarClientMode', next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }
  // Filtered project + folder lists driven by clientMode.
  const visibleProjects =
    clientMode && selectedId
      ? projects.filter((p) => p.id === selectedId)
      : projects
  const visibleFolders = clientMode
    ? folders.filter((f) => visibleProjects.some((p) => p.folder_id === f.id))
    : folders
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('collapsedFolders')
      return new Set<string>(raw ? (JSON.parse(raw) as string[]) : [])
    } catch {
      return new Set<string>()
    }
  })
  function toggleFolder(id: string) {
    setCollapsedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      try {
        localStorage.setItem('collapsedFolders', JSON.stringify([...next]))
      } catch {
        /* ignore */
      }
      return next
    })
  }
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const renameRef = useRef<HTMLInputElement | null>(null)
  // Inline "new project" composer state, opened from the + button next
  // to the « Projets · N » header. Kept local to the sidebar — the
  // parent only learns about it when the user confirms a name.
  const [creating, setCreating] = useState(false)
  const [createDraft, setCreateDraft] = useState('')
  const [creatingBusy, setCreatingBusy] = useState(false)
  const createRef = useRef<HTMLInputElement | null>(null)
  const confirm = useConfirm()

  useEffect(() => {
    if (creating && createRef.current) {
      createRef.current.focus()
    }
  }, [creating])

  useEffect(() => {
    if (creatingFolder && folderInputRef.current) folderInputRef.current.focus()
  }, [creatingFolder])

  async function commitCreate() {
    const name = createDraft.trim()
    if (!name || !onCreateProject) {
      setCreating(false)
      setCreateDraft('')
      return
    }
    setCreatingBusy(true)
    try {
      await onCreateProject(name)
      setCreating(false)
      setCreateDraft('')
    } finally {
      setCreatingBusy(false)
    }
  }

  function cancelCreate() {
    setCreating(false)
    setCreateDraft('')
  }

  useEffect(() => {
    if (renamingId && renameRef.current) {
      renameRef.current.focus()
      renameRef.current.select()
    }
  }, [renamingId])

  // Close the kebab menu on any outside click / Escape
  useEffect(() => {
    if (!menuFor) return
    function onDown() {
      setMenuFor(null)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuFor(null)
    }
    window.addEventListener('click', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('click', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [menuFor])

  function startRename(p: Project) {
    setMenuFor(null)
    setRenameDraft(p.name)
    setRenamingId(p.id)
  }

  // Read the value from the DOM at commit time so Enter/blur don't race with
  // onChange-driven state updates.
  const cancelBlurRef = useRef(false)
  function commitRenameFromInput(id: string, input: HTMLInputElement) {
    const next = input.value.trim()
    const prev = projects.find((p) => p.id === id)
    if (next && (!prev || next !== prev.name)) onRename(id, next)
    setRenamingId(null)
  }

  async function confirmDelete(p: Project) {
    setMenuFor(null)
    const ok = await confirm({
      title: t('sidebar.confirmDeleteTitle', { name: p.name }),
      message: t('sidebar.confirmDeleteMessage'),
      confirmLabel: t('sidebar.confirmDeleteButton'),
      requirePhrase: t('sidebar.confirmDeletePhrase'),
    })
    if (ok) onDelete(p.id)
  }

  /** Cancel an in-progress import. Re-uses the project-delete path: deleting
   *  the project cascades via FKs and wipes whatever messages/tickets were
   *  already ingested for it. */
  // dnd-kit sensor — same activation distance as KanbanBoard so a small
  // accidental drag during a click doesn't get interpreted as a reorder.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )
  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over) return
    const overId = String(over.id)
    const activeId = String(active.id)

    // A FOLDER is being dragged (by its grip) → reorder folders when
    // released over another folder.
    if (activeId.startsWith('folderdrag:')) {
      const fromId = activeId.slice('folderdrag:'.length)
      if (overId.startsWith('folder:') && overId !== 'folder:__root__') {
        const toId = overId.slice('folder:'.length)
        if (fromId !== toId) {
          const oldIndex = folders.findIndex((f) => f.id === fromId)
          const newIndex = folders.findIndex((f) => f.id === toId)
          if (oldIndex >= 0 && newIndex >= 0) {
            onReorderFolders?.(
              arrayMove(folders, oldIndex, newIndex).map((f) => f.id),
            )
          }
        }
      }
      return
    }

    // Dropped onto a folder drop-zone (header / body) or the root zone →
    // (re)assign the channel's folder.
    if (overId.startsWith('folder:')) {
      const fid = overId === 'folder:__root__' ? null : overId.slice(7)
      onAssignFolder?.(activeId, fid)
      return
    }

    // Dropped onto another channel row.
    if (activeId === overId) return
    const activeP = projects.find((p) => p.id === activeId)
    const overP = projects.find((p) => p.id === overId)
    // Joining a different group by dropping on one of its channels.
    if (activeP && overP && (activeP.folder_id ?? null) !== (overP.folder_id ?? null)) {
      onAssignFolder?.(activeId, overP.folder_id ?? null)
      return
    }
    // Same group → reorder.
    if (!onReorder) return
    const oldIndex = projects.findIndex((p) => p.id === activeId)
    const newIndex = projects.findIndex((p) => p.id === overId)
    if (oldIndex < 0 || newIndex < 0) return
    onReorder(arrayMove(projects, oldIndex, newIndex).map((p) => p.id))
  }

  async function commitCreateFolder() {
    const name = folderDraft.trim()
    setCreatingFolder(false)
    setFolderDraft('')
    if (name && onCreateFolder) await onCreateFolder(name)
  }

  async function confirmDeleteFolder(f: Folder) {
    setMenuFor(null)
    const ok = await confirm({
      title: t('sidebar.folder.confirmDeleteTitle', { name: f.name }),
      message: t('sidebar.folder.confirmDeleteMessage'),
      confirmLabel: t('sidebar.folder.confirmDeleteButton'),
    })
    if (ok) onDeleteFolder?.(f.id)
  }

  async function confirmCancelImport(p: Project) {
    const ok = await confirm({
      title: t('sidebar.confirmCancelImportTitle', { name: p.name }),
      message: t('sidebar.confirmCancelImportMessage'),
      confirmLabel: t('sidebar.cancelImport'),
    })
    if (ok) onDelete(p.id)
  }

  // ── Collapsed icon rail ─────────────────────────────────────────────
  if (collapsed) {
    return (
      <aside className="flex w-[60px] shrink-0 flex-col items-center border-r border-[var(--color-border-soft)] bg-[var(--color-surface)] transition-[width] duration-300 ease-in-out">
        {/* Logo only (no label) at the top — click to expand, mirroring the
            expanded brand which collapses on click. */}
        <button
          type="button"
          onClick={toggleCollapsed}
          title={t('sidebar.expand')}
          aria-label={t('sidebar.expand')}
          className="mt-5 mb-3 shrink-0 rounded-2xl transition-transform hover:scale-105"
        >
          <img
            src="/favicon.svg"
            alt="msgban"
            className="h-9 w-9 rounded-2xl shadow-[var(--shadow-soft)]"
          />
        </button>
        <div className="mb-1 h-px w-6 bg-[var(--color-border-soft)]" />
        <nav className="flex w-full flex-1 flex-col items-center gap-1.5 overflow-y-auto px-2 py-2">
          {projects.map((p) => {
            const isActive = selectedId === p.id
            const isBouncing = bouncingProjectIds?.has(p.id) ?? false
            const flame = projectFlames?.get(p.id) ?? null
            const isOwner = p.user_id === currentUserId
            const isPersonal = p.sender_id.startsWith('manual:')
            const newCount = newCountByProject[p.id] ?? 0
            return (
              <button
                key={p.id}
                type="button"
                data-channel-drop={p.id}
                data-channel-personal={isPersonal ? '1' : '0'}
                onClick={() => onSelect(p.id)}
                title={p.name}
                aria-label={p.name}
                className={cn(
                  'relative shrink-0 rounded-xl p-0.5 transition-all',
                  isActive && 'ring-2 ring-[var(--color-accent)] ring-offset-1 ring-offset-[var(--color-surface)]',
                  isBouncing && 'animate-project-bounce',
                  cardDragActive &&
                    (isPersonal
                      ? 'ring-2 ring-[var(--color-accent)]/50'
                      : 'pointer-events-none opacity-30 grayscale'),
                )}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-xl text-[11px] font-semibold',
                    projectBubbleClass(p.color, avatarColor(p.id)),
                  )}
                >
                  {initials(p.name)}
                </span>
                {flame && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--color-surface)] ring-2 ring-[var(--color-surface)]">
                    <Flame
                      className={cn(
                        'h-3.5 w-3.5',
                        flame === 'red' ? 'text-red-500' : 'text-orange-500',
                      )}
                      strokeWidth={2}
                    />
                  </span>
                )}
                {!isOwner && (
                  <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[var(--color-accent)] text-white ring-2 ring-[var(--color-surface)]">
                    <Users className="h-2 w-2" strokeWidth={3} />
                  </span>
                )}
                {newCount > 0 && !flame && (
                  <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[var(--color-accent)] ring-2 ring-[var(--color-surface)]" />
                )}
              </button>
            )
          })}
          {onCreateProject && (
            <button
              type="button"
              onClick={toggleCollapsed}
              title={t('sidebar.newProject')}
              aria-label={t('sidebar.newProject')}
              className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-dashed border-[var(--color-border)] text-[var(--color-text-dim)] transition-colors hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </nav>
        {/* Footer — expand toggle (bottom) + version, mirroring expanded. */}
        <div className="flex w-full flex-col items-center gap-1 border-t border-[var(--color-border-soft)] py-2">
          <button
            type="button"
            onClick={toggleCollapsed}
            title={t('sidebar.expand')}
            aria-label={t('sidebar.expand')}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
          <span className="text-[8px] tabular-nums text-[var(--color-text-dim)]">
            v{APP_VERSION}
          </span>
        </div>
      </aside>
    )
  }

  return (
    <aside className="flex w-64 shrink-0 flex-col border-r border-[var(--color-border-soft)] bg-[var(--color-surface)] transition-[width] duration-300 ease-in-out">
      {/* Brand */}
      <div className="flex items-center gap-2.5 px-5 pt-5 pb-4">
        <button
          type="button"
          onClick={toggleCollapsed}
          title={t('sidebar.collapse')}
          aria-label={t('sidebar.collapse')}
          className="shrink-0 rounded-2xl transition-transform hover:scale-105"
        >
          <img
            src="/favicon.svg"
            alt="msgban"
            className="h-9 w-9 rounded-2xl shadow-[var(--shadow-soft)]"
          />
        </button>
        <div className="min-w-0">
          <div className="text-[15px] font-semibold leading-tight text-[var(--color-text-strong)]">
            msgban
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
            {t('sidebar.brandTagline')}
          </div>
        </div>
      </div>

      <div className="mx-5 border-t border-[var(--color-border-soft)]" />

      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
          {t('sidebar.projectsCount', { count: projects.length })}
        </span>
        <div className="-mr-1 flex items-center gap-0.5">
          {onCreateFolder && !creatingFolder && (
            <button
              type="button"
              onClick={() => setCreatingFolder(true)}
              aria-label={t('sidebar.folder.newFolder')}
              title={t('sidebar.folder.newFolder')}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
            >
              <FolderPlus className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={toggleClientMode}
            aria-pressed={clientMode}
            aria-label={t(
              clientMode ? 'sidebar.clientModeOff' : 'sidebar.clientModeOn',
            )}
            title={t(
              clientMode ? 'sidebar.clientModeOff' : 'sidebar.clientModeOn',
            )}
            className={cn(
              'inline-flex h-5 w-5 items-center justify-center rounded transition-colors',
              clientMode
                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
            )}
          >
            <Focus className="h-3.5 w-3.5" />
          </button>
          {onCreateProject && !creating && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              aria-label={t('sidebar.newProject')}
              title={t('sidebar.newProject')}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {creatingFolder && (
        <div className="mx-3 mb-1.5 rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface-2)] p-2 shadow-[var(--shadow-soft)]">
          <input
            ref={folderInputRef}
            value={folderDraft}
            onChange={(e) => setFolderDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void commitCreateFolder()
              }
              if (e.key === 'Escape') {
                setCreatingFolder(false)
                setFolderDraft('')
              }
            }}
            placeholder={t('sidebar.folder.namePlaceholder')}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-[12.5px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
          />
          <div className="mt-1.5 flex gap-1.5">
            <button
              type="button"
              onClick={() => void commitCreateFolder()}
              disabled={!folderDraft.trim()}
              className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-accent)] px-2.5 py-1 text-[11.5px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:opacity-50"
            >
              <Check className="h-3 w-3" />
              {t('common.create')}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreatingFolder(false)
                setFolderDraft('')
              }}
              className="rounded-lg p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {creating && (
        <div className="mx-3 mb-1.5 rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface-2)] p-2 shadow-[var(--shadow-soft)]">
          <input
            ref={createRef}
            value={createDraft}
            onChange={(e) => setCreateDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitCreate()
              }
              if (e.key === 'Escape') cancelCreate()
            }}
            disabled={creatingBusy}
            placeholder={t('sidebar.projectNamePlaceholder')}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-[12.5px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)] disabled:opacity-60"
          />
          <div className="mt-1.5 flex gap-1.5">
            <button
              type="button"
              onClick={commitCreate}
              disabled={creatingBusy || !createDraft.trim()}
              className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-accent)] px-2.5 py-1 text-[11.5px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:opacity-50"
            >
              {creatingBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Check className="h-3 w-3" />
              )}
              {t('common.create')}
            </button>
            <button
              type="button"
              onClick={cancelCreate}
              disabled={creatingBusy}
              className="rounded-lg p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-2">
        {projects.length === 0 && !creating && (
          <div className="mx-2 mt-2 rounded-2xl border border-dashed border-[var(--color-border)] px-3 py-5 text-center text-[11.5px] leading-relaxed text-[var(--color-text-dim)] whitespace-pre-line">
            {showArchived ? t('sidebar.noProjects') : t('sidebar.noActiveProjects')}
          </div>
        )}
        <DndContext
          sensors={dndSensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={visibleProjects.map((p) => p.id)}
            strategy={verticalListSortingStrategy}
          >
        {(() => {
          const renderRow = (p: Project) => {
          const count = ticketCounts[p.id] ?? 0
          const newCount = newCountByProject[p.id] ?? 0
          const isActive = selectedId === p.id
          const isRenaming = renamingId === p.id
          const menuOpen = menuFor === p.id
          const isOwner = p.user_id === currentUserId
          const isBouncing = bouncingProjectIds?.has(p.id) ?? false
          const flame = projectFlames?.get(p.id) ?? null
          // Personal (manual) channels can receive a card dropped from the
          // board; real WhatsApp-number channels cannot.
          const isPersonal = p.sender_id.startsWith('manual:')

          return (
            <SortableProjectRow key={p.id} id={p.id}>
              {(dragHandleProps) => (
              <>
              <div
                data-channel-drop={p.id}
                data-channel-personal={isPersonal ? '1' : '0'}
                className={cn(
                  'group flex w-full items-center gap-1.5 rounded-xl px-1.5 py-2 transition-all',
                  isActive
                    ? 'bg-[var(--color-surface-2)]'
                    : 'hover:bg-[var(--color-surface-2)]/70',
                  p.archived && 'opacity-60',
                  // While a card is dragged: light up droppable (personal)
                  // channels, grey out the ones that can't accept a card.
                  cardDragActive &&
                    (isPersonal
                      ? 'ring-1 ring-[var(--color-accent)]/50 ring-inset'
                      : 'pointer-events-none opacity-30 grayscale'),
                )}
              >
                {/* Drag handle — visible on hover, otherwise transparent so
                    the row's left edge stays clean. Only this consumes the
                    dnd listeners; the rest of the row (avatar, title, kebab)
                    keeps its native click handlers. */}
                <span
                  {...dragHandleProps}
                  title={t('sidebar.dragToReorder')}
                  aria-label={t('sidebar.reorderProject')}
                  className="-ml-0.5 shrink-0 cursor-grab touch-none rounded p-0.5 text-[var(--color-text-dim)] opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-60 active:cursor-grabbing"
                >
                  <GripVertical className="h-3.5 w-3.5" />
                </span>
                <button
                  type="button"
                  onClick={() => !isRenaming && onSelect(p.id)}
                  onDoubleClick={(e) => {
                    // Direct double-click rename — no need to dig into the
                    // kebab menu for what's a frequent gesture.
                    if (!isRenaming) {
                      e.preventDefault()
                      startRename(p)
                    }
                  }}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  <div
                    className={cn(
                      'relative shrink-0',
                      isBouncing && 'animate-project-bounce',
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-xl text-[11px] font-semibold',
                        projectBubbleClass(p.color, avatarColor(p.id)),
                      )}
                    >
                      {initials(p.name)}
                    </div>
                    {/* "Shared with you" badge — corner overlay (Google-Drive
                        style) so the row stays compact and the indicator
                        sticks visually to the project identity. */}
                    {!isOwner && (
                      <span
                        title={t('sidebar.sharedWithYou')}
                        aria-label={t('sidebar.sharedWithYou')}
                        className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[var(--color-accent)] text-white ring-2 ring-[var(--color-surface)]"
                      >
                        <Users className="h-2 w-2" strokeWidth={3} />
                      </span>
                    )}
                    {/* Priority flame — red when the project has an urgent
                        card, orange when it only has a high-priority one
                        (urgent wins). Top-right corner so it doesn't clash
                        with the bottom-right "shared" badge. */}
                    {flame && (
                      <span
                        title={
                          flame === 'red'
                            ? t('sidebar.hasUrgent')
                            : t('sidebar.hasHigh')
                        }
                        aria-label={
                          flame === 'red'
                            ? t('sidebar.hasUrgent')
                            : t('sidebar.hasHigh')
                        }
                        className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--color-surface)] ring-2 ring-[var(--color-surface)]"
                      >
                        <Flame
                          className={cn(
                            'h-3.5 w-3.5',
                            flame === 'red' ? 'text-red-500' : 'text-orange-500',
                          )}
                          strokeWidth={2}
                        />
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {isRenaming ? (
                      <input
                        ref={renameRef}
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                          if (cancelBlurRef.current) {
                            cancelBlurRef.current = false
                            setRenamingId(null)
                            return
                          }
                          commitRenameFromInput(p.id, e.currentTarget)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            commitRenameFromInput(p.id, e.currentTarget)
                          }
                          if (e.key === 'Escape') {
                            cancelBlurRef.current = true
                            setRenamingId(null)
                          }
                        }}
                        className="w-full rounded-md border border-[var(--color-link)] bg-[var(--color-surface)] px-1.5 py-0.5 text-[13px] text-[var(--color-text-strong)] outline-none"
                      />
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5">
                          <span
                            title={p.name}
                            className={cn(
                              'truncate text-[13px]',
                              isActive
                                ? 'font-medium text-[var(--color-text-strong)]'
                                : 'text-[var(--color-text)]',
                            )}
                          >
                            {p.name}
                          </span>
                          {p.import_status === 'processing' && (
                            <Loader2
                              className="h-3 w-3 shrink-0 animate-spin text-[var(--color-link)]"
                              aria-label={t('sidebar.compileInProgress')}
                            />
                          )}
                          {p.import_status === 'error' && (
                            <AlertCircle
                              className="h-3 w-3 shrink-0 text-[var(--color-danger)]"
                              aria-label={p.import_error ?? t('sidebar.compileError')}
                            />
                          )}
                          {p.archived && (
                            <span className="shrink-0 rounded-full bg-[var(--color-surface-3)] px-1.5 py-0.5 text-[8.5px] uppercase tracking-wide text-[var(--color-text-dim)]">
                              {t('sidebar.archived')}
                            </span>
                          )}
                        </div>
                        {/* Status row is only shown when import is processing
                            or failed — the sender's phone number was noise. */}
                        {(p.import_status === 'processing' ||
                          p.import_status === 'error') && (
                          <div className="flex items-center justify-between gap-1.5">
                            <div className="min-w-0 flex-1 truncate text-[10.5px] text-[var(--color-text-dim)]">
                              {p.import_status === 'processing'
                                ? `${p.import_stage ?? t('sidebar.compiling')} ${p.import_progress}%`
                                : t('sidebar.compileFailed')}
                            </div>
                            {p.import_status === 'processing' && isOwner && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  confirmCancelImport(p)
                                }}
                                aria-label={t('sidebar.cancelImport')}
                                title={t('sidebar.cancelImport')}
                                className="inline-flex shrink-0 items-center gap-0.5 rounded-md px-1 py-0.5 text-[10px] font-medium text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger-bg)]"
                              >
                                <StopCircle className="h-3 w-3" />
                                {t('sidebar.cancelImportShort')}
                              </button>
                            )}
                          </div>
                        )}
                        {p.import_status === 'processing' && (
                          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-[var(--color-surface-3)]">
                            <div
                              className="h-full rounded-full bg-[var(--color-accent)] transition-[width] duration-700 ease-out"
                              style={{ width: `${Math.max(p.import_progress, 4)}%` }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </button>

                {isRenaming ? (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => {
                        if (renameRef.current) {
                          commitRenameFromInput(p.id, renameRef.current)
                        }
                      }}
                      className="rounded-md p-1 text-[var(--color-accent)] hover:bg-[var(--color-accent-bg)]"
                      aria-label={t('common.validate')}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setRenamingId(null)}
                      className="rounded-md p-1 text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)]"
                      aria-label={t('common.cancel')}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex shrink-0 items-center gap-1">
                    {newCount > 0 && (
                      <span
                        title={t('sidebar.newCardsTooltip', { count: newCount })}
                        className="rounded-full bg-[var(--color-accent)] px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white"
                      >
                        +{newCount}
                      </span>
                    )}
                    {count > 0 && (
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
                          isActive
                            ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                            : 'bg-[var(--color-surface-2)] text-[var(--color-text-dim)]',
                        )}
                      >
                        {count}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setMenuFor(menuOpen ? null : p.id)
                      }}
                      className={cn(
                        'rounded-md p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
                        menuOpen
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100',
                      )}
                      aria-label={t('sidebar.projectOptions')}
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Kebab menu */}
              {menuOpen && (
                <div
                  className="absolute right-2 top-[calc(100%-4px)] z-20 w-44 overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] py-1 shadow-[var(--shadow-modal)]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem icon={<Pencil className="h-3.5 w-3.5" />} onClick={() => startRename(p)}>
                    {t('common.rename')}
                  </MenuItem>
                  {onOpenSettings && (
                    <MenuItem
                      icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
                      onClick={() => {
                        setMenuFor(null)
                        onOpenSettings(p.id)
                      }}
                    >
                      {t('projectSettings.menuItem')}
                    </MenuItem>
                  )}
                  {onInviteToCards && (
                    <MenuItem
                      icon={<UserPlus2 className="h-3.5 w-3.5" />}
                      onClick={() => {
                        setMenuFor(null)
                        onInviteToCards(p.id)
                      }}
                    >
                      {t('sidebar.inviteCardsMenu')}
                    </MenuItem>
                  )}
                  <MenuItem
                    icon={
                      p.archived ? (
                        <ArchiveRestore className="h-3.5 w-3.5" />
                      ) : (
                        <Archive className="h-3.5 w-3.5" />
                      )
                    }
                    onClick={() => {
                      setMenuFor(null)
                      onArchive(p.id, !p.archived)
                    }}
                  >
                    {p.archived ? t('common.unarchive') : t('common.archive')}
                  </MenuItem>
                  {isOwner && (
                    <>
                      <div className="my-1 border-t border-[var(--color-border-soft)]" />
                      <MenuItem
                        icon={<Trash2 className="h-3.5 w-3.5" />}
                        danger
                        onClick={() => confirmDelete(p)}
                      >
                        {t('common.delete')}
                      </MenuItem>
                    </>
                  )}
                </div>
              )}
              </>
              )}
            </SortableProjectRow>
          )
          }

          // « Ungrouped » = no folder OR a folder the current user can't
          // see. Folders are a PER-USER organisation layer but the
          // assignment lives on the shared project row (folder_id), so an
          // invited collaborator inherits the OWNER's folder_id pointing at
          // a folder that isn't in their own `folders` list. Without this
          // fallback the project would belong to no visible folder AND be
          // excluded from the root list → it vanishes entirely, leaving the
          // invitee staring at just the « Channels · N » header. Treat the
          // dangling reference as ungrouped so the project still renders.
          const visibleFolderIds = new Set(visibleFolders.map((f) => f.id))
          const ungrouped = visibleProjects.filter(
            (p) => !p.folder_id || !visibleFolderIds.has(p.folder_id),
          )
          return (
            <>
              {visibleFolders.map((f) => {
                const inFolder = visibleProjects.filter((p) => p.folder_id === f.id)
                // Roll the per-channel new-card counts up to the folder
                // header so a collapsed folder still announces activity
                // happening inside it — otherwise a new message lands
                // silently the moment the user has closed the folder.
                const folderNewCount = inFolder.reduce(
                  (acc, p) => acc + (newCountByProject[p.id] ?? 0),
                  0,
                )
                return (
                  <FolderSection
                    key={f.id}
                    folder={f}
                    count={inFolder.length}
                    newCount={folderNewCount}
                    collapsed={collapsedFolders.has(f.id)}
                    menuOpen={menuFor === `folder:${f.id}`}
                    onToggle={() => toggleFolder(f.id)}
                    onMenuToggle={() =>
                      setMenuFor(
                        menuFor === `folder:${f.id}` ? null : `folder:${f.id}`,
                      )
                    }
                    onRename={(name) => onRenameFolder?.(f.id, name)}
                    onSetColor={(c) => onSetFolderColor?.(f.id, c)}
                    onDelete={() => confirmDeleteFolder(f)}
                    t={t}
                  >
                    {inFolder.map(renderRow)}
                  </FolderSection>
                )
              })}
              <RootDroppable
                hasFolders={folders.length > 0}
                hasChildren={ungrouped.length > 0}
              >
                {ungrouped.map(renderRow)}
              </RootDroppable>
            </>
          )
        })()}
          </SortableContext>
        </DndContext>
      </nav>

      {/* Archived toggle */}
      {(archivedCount > 0 || showArchived) && (
        <div className="border-t border-[var(--color-border-soft)] px-3 py-2">
          <button
            type="button"
            onClick={onToggleArchived}
            className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-[11.5px] text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
          >
            <Archive className="h-3.5 w-3.5" />
            {showArchived
              ? t('sidebar.hideArchived')
              : t('sidebar.showArchived', { count: archivedCount })}
          </button>
        </div>
      )}

      {/* Footer — collapse toggle (bottom-left) + version */}
      <div className="flex items-center gap-2 border-t border-[var(--color-border-soft)] px-3 py-2">
        <button
          type="button"
          onClick={toggleCollapsed}
          title={t('sidebar.collapse')}
          aria-label={t('sidebar.collapse')}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
        <span className="text-[10px] tabular-nums tracking-wide text-[var(--color-text-dim)]">
          msgban&nbsp;v{APP_VERSION}
        </span>
      </div>
    </aside>
  )
}

function MenuItem({
  icon,
  children,
  onClick,
  danger = false,
}: {
  icon: React.ReactNode
  children: React.ReactNode
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[12.5px] transition-colors',
        danger
          ? 'text-[var(--color-danger)] hover:bg-[var(--color-danger-bg)]'
          : 'text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
      )}
    >
      <span className={danger ? '' : 'text-[var(--color-text-dim)]'}>{icon}</span>
      {children}
    </button>
  )
}

/** dnd-kit wrapper for a single project row in the sidebar. Provides the
 *  drag-handle props via render-children so only the small GripVertical
 *  icon on the left becomes a drag target — clicks on the row body and
 *  buttons inside it still work normally. */
function SortableProjectRow({
  id,
  children,
}: {
  id: string
  children: (dragHandleProps: Record<string, unknown>) => ReactNode
}) {
  const {
    setNodeRef,
    transform,
    transition,
    attributes,
    listeners,
    isDragging,
  } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('relative', isDragging && 'z-10 opacity-70')}
    >
      {children({ ...attributes, ...listeners })}
    </div>
  )
}

type TFn = ReturnType<typeof useTranslation>['t']

/** A collapsible sidebar folder. The whole block is a dnd drop-zone:
 *  dropping a channel anywhere on it files the channel into the folder. */
function FolderSection({
  folder,
  count,
  newCount,
  collapsed,
  menuOpen,
  onToggle,
  onMenuToggle,
  onRename,
  onSetColor,
  onDelete,
  t,
  children,
}: {
  folder: Folder
  count: number
  /** Sum of new cards (across all child channels of this folder) within
   *  the user's new-card window. Drives the « +N » header chip so a
   *  collapsed folder still surfaces activity inside it. */
  newCount: number
  collapsed: boolean
  menuOpen: boolean
  onToggle: () => void
  onMenuToggle: () => void
  onRename: (name: string) => void
  onSetColor: (color: string | null) => void
  onDelete: () => void
  t: TFn
  children: ReactNode
}) {
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `folder:${folder.id}`,
  })
  // The folder is also draggable (by its grip) to reorder folders.
  const {
    setNodeRef: setDragRef,
    attributes,
    listeners,
    transform,
    isDragging,
  } = useDraggable({ id: `folderdrag:${folder.id}` })
  const setRoot = (node: HTMLDivElement | null) => {
    setDropRef(node)
    setDragRef(node)
  }
  const colorClass = folder.color
    ? (LIST_COLORS.find((c) => c.key === folder.color)?.dot.replace('bg-', 'text-') ??
      'text-[var(--color-text-dim)]')
    : 'text-[var(--color-text-dim)]'
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(folder.name)
  const inputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [renaming])
  function commit() {
    const n = draft.trim()
    setRenaming(false)
    if (n && n !== folder.name) onRename(n)
    else setDraft(folder.name)
  }
  return (
    <div
      ref={setRoot}
      style={{ transform: CSS.Translate.toString(transform) }}
      className={cn(
        'mb-0.5 rounded-xl transition-colors',
        isDragging && 'z-10 opacity-70',
        isOver && 'bg-[var(--color-accent-bg)]/60 ring-1 ring-[var(--color-accent)]/40',
      )}
    >
      <div className="group/folder relative flex items-center gap-1 rounded-xl px-1.5 py-1.5">
        {/* Drag handle — reorder folders (hover-visible grip). */}
        <span
          {...attributes}
          {...listeners}
          title={t('sidebar.dragToReorder')}
          className="-ml-0.5 shrink-0 cursor-grab touch-none rounded p-0.5 text-[var(--color-text-dim)] opacity-0 transition-opacity hover:opacity-100 group-hover/folder:opacity-60 active:cursor-grabbing"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </span>
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 rounded p-0.5 text-[var(--color-text-dim)] transition-colors hover:text-[var(--color-text)]"
          aria-label={t('sidebar.folder.toggle')}
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </button>
        {collapsed ? (
          <FolderIcon className={cn('h-4 w-4 shrink-0', colorClass)} />
        ) : (
          <FolderOpen className={cn('h-4 w-4 shrink-0', colorClass)} />
        )}
        {renaming ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commit()
              }
              if (e.key === 'Escape') {
                setDraft(folder.name)
                setRenaming(false)
              }
            }}
            className="min-w-0 flex-1 rounded-md border border-[var(--color-link)] bg-[var(--color-surface)] px-1.5 py-0.5 text-[12px] font-medium text-[var(--color-text-strong)] outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={onToggle}
            onDoubleClick={() => {
              setDraft(folder.name)
              setRenaming(true)
            }}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          >
            <span className="truncate text-[12px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
              {folder.name}
            </span>
            <span className="shrink-0 text-[10px] tabular-nums text-[var(--color-text-dim)]">
              {count}
            </span>
            {newCount > 0 && (
              <span
                title={t('sidebar.newCardsTooltip', { count: newCount })}
                className="shrink-0 rounded-full bg-[var(--color-accent)] px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white"
              >
                +{newCount}
              </span>
            )}
          </button>
        )}
        {!renaming && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onMenuToggle()
            }}
            className={cn(
              'shrink-0 rounded-md p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
              menuOpen ? 'opacity-100' : 'opacity-0 group-hover/folder:opacity-100',
            )}
            aria-label={t('sidebar.folder.options')}
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        )}
        {menuOpen && (
          <div
            className="absolute right-2 top-[calc(100%-2px)] z-20 w-44 overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] py-1 shadow-[var(--shadow-modal)]"
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem
              icon={<Pencil className="h-3.5 w-3.5" />}
              onClick={() => {
                onMenuToggle()
                setDraft(folder.name)
                setRenaming(true)
              }}
            >
              {t('common.rename')}
            </MenuItem>
            <div className="my-1 border-t border-[var(--color-border-soft)]" />
            {/* Colour picker — 6×2 swatch grid (saturated dot). */}
            <div className="grid grid-cols-6 gap-1.5 px-3 py-2">
              {LIST_COLORS.map((c) => {
                const selected = folder.color === c.key
                return (
                  <button
                    key={c.key}
                    type="button"
                    title={c.label}
                    aria-pressed={selected}
                    onClick={() => {
                      onMenuToggle()
                      onSetColor(selected ? null : c.key)
                    }}
                    className={cn(
                      'relative inline-flex h-5 w-5 items-center justify-center rounded-full text-white transition-transform hover:z-10 hover:scale-110',
                      c.dot,
                    )}
                  >
                    {selected && <Check className="h-3 w-3" strokeWidth={3} />}
                  </button>
                )
              })}
            </div>
            <div className="my-1 border-t border-[var(--color-border-soft)]" />
            <MenuItem
              icon={<Trash2 className="h-3.5 w-3.5" />}
              danger
              onClick={onDelete}
            >
              {t('common.delete')}
            </MenuItem>
          </div>
        )}
      </div>
      {!collapsed && <div className="space-y-0.5 pl-3">{children}</div>}
    </div>
  )
}

/** Drop-zone for the ungrouped channels (root). Dropping a channel here
 *  removes it from any folder. When folders exist above, a labelled
 *  divider marks the boundary so loose channels don't blend into the
 *  last folder. */
function RootDroppable({
  hasFolders,
  hasChildren,
  children,
}: {
  hasFolders: boolean
  hasChildren: boolean
  children: ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({ id: 'folder:__root__' })
  return (
    <div>
      {hasFolders && (
        <div className="mx-1.5 mb-1 mt-3 h-px bg-[var(--color-border-soft)]" />
      )}
      <div
        ref={setNodeRef}
        className={cn(
          'space-y-0.5 rounded-xl transition-colors',
          // Give the empty root zone enough height to be a comfortable
          // drop target when there are folders but no loose channels.
          hasFolders && !hasChildren && 'min-h-[36px]',
          isOver && 'bg-[var(--color-accent-bg)]/60 ring-1 ring-[var(--color-accent)]/40',
        )}
      >
        {children}
      </div>
    </div>
  )
}
